import { AutogenLayoutDriverInformation } from "./autogen/AutogenLayoutDriverInformation";
import {Model, ModelRow, StringUtil} from "@mcleod/core";
import {
    BlurEvent, ChangeEvent, DataSourceExecutionEvent, LookupModelSearchEvent, LookupModelSelectionEvent
} from "@mcleod/components";
import { ContactVerificationUtil } from "@mcleod/powerbroker/src/ContactVerificationUtil";

export class DriverInformation extends AutogenLayoutDriverInformation {
    public afterDriverSelection: (selectedRow: ModelRow, driverNumberSuffx: string) => void;

    override onLoad(): void {
        Model.searchSingleRecord("lme/powerbroker/br-tracking-ctrl", { vendor: "M", is_active: "Y" }).then(response => {
            const isInternational = response?.get("international_tracking") === "Y" || false;
            this.textboxOverrideDrvrCountryCode.visible = isInternational;
            this.textboxOverrideDrvr2CountryCode.visible = isInternational;
        });
    }

    public enableDriverTwoComponents() {
        if (this.activeRow == null) return;
        const isDriverOneEmpty: boolean = StringUtil.isEmptyString(this.activeRow?.get("override_driver_nm"));
        this.textboxOverrideDriver2Nm.enabled = !isDriverOneEmpty;
        this.textboxOverrideDrvr2CountryCode.enabled = !isDriverOneEmpty;
        this.textboxOverrideDrvr2Cell.enabled = !isDriverOneEmpty;
        this.textboxOverrideDrvr2Email.enabled = !isDriverOneEmpty;
        if (isDriverOneEmpty) {
            this.activeRow.set("override_driver2_id", null);
            this.activeRow.set("override_driver2_nm", null);
            this.activeRow.set("override_drvr2_cell", null);
            this.activeRow.set("override_drvr2_email", null);
            this.activeRow.set("override_drvr2_country_code", null);
            this.textboxOverrideDriver2Nm.clear();
            this.textboxOverrideDrvr2CountryCode.clear();
            this.textboxOverrideDrvr2Cell.clear();
            this.textboxOverrideDrvr2Email.clear();
        }
    }

    setDriverData(selectedRow: ModelRow, driverNumberSuffix: string) {
        const movementTractorNumber = this.activeRow?.get("carrier_tractor");
        const carrierDriverTractor = selectedRow?.get("tractor_number");
        this.activeRow.set(`override_driver${driverNumberSuffix}_id`, selectedRow?.get("id"));
        this.activeRow.set(`override_drvr${driverNumberSuffix}_cell`, selectedRow?.get("cell_phone_number"));
        this.activeRow.set(`override_drvr${driverNumberSuffix}_email`, selectedRow?.get("email_address"));
        this.activeRow.set(`override_drvr${driverNumberSuffix}_country_code`, selectedRow?.get("country_code"));
        this.dynamicallySetCarrierTractor(driverNumberSuffix, carrierDriverTractor, movementTractorNumber);
        this.enableDriverTwoComponents();
        this.mainDataSource.displayDataInBoundComponents();
        this.afterDriverSelection?.(selectedRow, driverNumberSuffix);
    }

    private dynamicallySetCarrierTractor(driverNumberSuffix: any, carrierDriverTractor: any, movementTractorNumber: any) {
        if (driverNumberSuffix === "") {
            this.activeRow.set(`carrier_tractor`, carrierDriverTractor);
        } else if (driverNumberSuffix === "2") {
            // When driver one does not have tractor
            if (StringUtil.isEmptyString(movementTractorNumber)) {
                this.mainDataSource.activeRow.set(`carrier_tractor`, carrierDriverTractor);
            } else {
                this.mainDataSource.activeRow.set(`carrier_tractor`, movementTractorNumber);
            }
        }
    }

    sourceMovementAfterExecution(event: DataSourceExecutionEvent) {
        const isAssigned: boolean = !this.activeRow?.isNull("override_payee_id");
        this.textboxCarrierContact.visible = isAssigned;
        this.textboxCarrierEmail.visible = isAssigned;
        this.textboxCarrierPhone.visible = isAssigned;
        this.imageCarrierVerification.visible = isAssigned;
        ContactVerificationUtil.updateVerificationImage(this.activeRow?.getFirstLookupModelData("carrier_contact"), this.imageCarrierVerification);
        this.enableDriverTwoComponents();
    }

    setCarrierContact(selectedRow: ModelRow<any>) {
        this.activeRow.set("carrier_phone", selectedRow?.get("phone"));
        this.activeRow.set("carrier_email", selectedRow?.get("email"));
        ContactVerificationUtil.updateVerificationImage(selectedRow, this.imageCarrierVerification);
    }

    textboxCarrierContactBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.parent_row_id = this.mainDataSource.activeRow?.get("override_payee_id");
        event.filter.parent_row_type = "P";
    }

    textboxCarrierContactOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() == true) {
            this.setCarrierContact(event.selection);
        }
    }

    textboxCarrierContactOnBlur(event: BlurEvent) {
        const modelRow = this.textboxCarrierContact.getFirstLookupModelData();
        if (event.changedWhileFocused || StringUtil.isEmptyString(this.textboxCarrierContact.text)) {
            this.setCarrierContact(modelRow);
        }
    }

    textboxOverrideDriver1NmBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        const payeeId = this.activeRow.get("override_payee_id", this.activeRow.get("pending_payee_id"));
        if (StringUtil.isEmptyString(payeeId)) {
            event.preventDefault();
        }
        event.filter.carrier_id = payeeId;
        event.filter.is_active = "Y";
    }

    textboxOverrideDriver2NmBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        const payeeId = this.activeRow.get("override_payee_id", this.activeRow.get("pending_payee_id"));
        if (StringUtil.isEmptyString(payeeId)) {
            event.preventDefault();
        }
        event.filter.carrier_id = payeeId;
        event.filter.is_active = "Y";
    }

    textboxOverrideDriver1NmOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() == true) {
            this.setDriverData(event.selection, "");
        }
    }

    textboxOverrideDriver2NmOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() == true) {
            this.setDriverData(event.selection, "2");
        }
    }

    textboxOverrideDriver1NmOnBlur(event: BlurEvent) {
        const modelRow = this.textboxOverrideDriver1Nm.getFirstLookupModelData();
        if (this.textboxOverrideDriver1Nm.isEmpty() && (event.changedWhileFocused && !this.textboxOverrideDriver1Nm.isDropdownVisible())) {
            this.setDriverData(modelRow, "");
        }
    }

    textboxOverrideDriver2NmOnBlur(event: BlurEvent) {
        const modelRow = this.textboxOverrideDriver2Nm.getFirstLookupModelData();
        if (this.textboxOverrideDriver2Nm.isEmpty() && event.changedWhileFocused && !this.textboxOverrideDriver1Nm.isDropdownVisible()) {
            this.setDriverData(modelRow, "2");
        }
    }

    textboxOverrideDriver1NmOnChange(event: ChangeEvent) {
        this.enableDriverTwoComponents();
    }
}
